import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as API from "../api/index";
import * as Yup from "yup";
import { NotificationManager } from "react-notifications";

import { TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import ConfirmAddDialog from "../util/confirmAddDialog";
import AddFormLayout from "../shared/AddFormLayout";
import SectionGrid from "../shared/SectionGrid";
import { useSectionedForm } from "../shared/useSectionedForm";

const FORM_SECTIONS = [
  {
    id: "basic",
    label: "Basic info",
    fields: ["TRANSFER_NAME", "STATE", "PROJECT_ID", "FREQUENCY", "CMD", "CMD_GROUP"],
  },
  {
    id: "source-dest",
    label: "Source & destination",
    fields: ["SOURCE", "DESTINATION", "FILTER"],
  },
  {
    id: "paths",
    label: "Paths",
    fields: ["FROM_PATH", "TO_PATH"],
  },
  {
    id: "rules",
    label: "Processing rules",
    fields: [
      "ARCHIVE",
      "FILE_MASK",
      "PROCESS",
      "REQUIRED_FL",
      "OVERWRITE_FL",
      "SUBFOLDERS_FL",
      "LIST_LIMIT",
      "LIST_ORDER",
      "REPLACES",
    ],
  },
];

const ConnectionCommandForm = () => {
  const validationSchema = Yup.object().shape({
    TRANSFER_NAME: Yup.string().required("Connection ID is required"),
    STATE: Yup.string().required("Connection ID is required"),
    PROJECT_ID: Yup.string().required("Project ID is required"),
    FREQUENCY: Yup.string().required("Project ID is required"),
    CMD: Yup.string().required("Project OID is required"),
    CMD_GROUP: Yup.string().required("Project Name is required"),
    SOURCE: Yup.string().required("Sponsor Name is required"),
    DESTINATION: Yup.string().required("Business ID is required"),
    FILTER: Yup.string().required("Connection URL is required"),
    FROM_PATH: Yup.string().required("Connection Provider is required"),
    TO_PATH: Yup.string().required("Connection Data is required"),
    ARCHIVE: Yup.string().required("Auth Type is required"),
    FILE_MASK: Yup.string().required("Auth ID is required"),
    PROCESS: Yup.string().required("Auth PIN is required"),
    REQUIRED_FL: Yup.string().required("Ath  passcode is required"),
    OVERWRITE_FL: Yup.string().required("State is required"),
    SUBFOLDERS_FL: Yup.string().nullable().notRequired().default(""),
    LIST_LIMIT: Yup.string().nullable().notRequired().default(""),
    LIST_ORDER: Yup.string().nullable().notRequired().default(""),
    REPLACES: Yup.string().nullable().notRequired().default(""),
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { sections, activeId, setActiveId, isFirst, isLast, goNext, goPrev } =
    useSectionedForm(FORM_SECTIONS, errors);

  const location = useLocation();
  const navigate = useNavigate();
  const transferMetaData = location.state;

  const stateItems = ["Active", "Disable"];
  const frequencyItems = ["HOURLY", "DAILY", "WEEKLY", "MONTHLY"];
  const itemsCommand = [
    "copy",
    "move",
    "convert",
    "csvsubset",
    "rename",
    "rezip",
    "unzip",
    "transform",
  ];
  const itemsArchive = ["true", "false"];
  const itemsFileMask = ["true", "false"];
  const itemsFlags = ["0", "1"];

  const onClickFileTrns = () => {
    navigate("/FileTransfers");
  };

  const transferID = transferMetaData.transferID;
  const transfer_seq = transferMetaData.transfer_seq;
  const [pendingPayload, setPendingPayload] = useState(null);

  const onSubmit = (data) => {
    const payload = [
      {
        TRANSFER_ID: transferID,
        TRANSFER_SEQ: transfer_seq,
        PARENT_SEQ: 0,
        ...data,
      },
    ];

    let finalPayload;

    if (transferMetaData.original_data) {
      finalPayload = transferMetaData.original_data;
      finalPayload.push(...payload);
    } else {
      finalPayload = payload;
    }

    setPendingPayload(finalPayload);
  };

  const handleConfirmAdd = () => {
    const finalPayload = pendingPayload;
    setPendingPayload(null);
    const resourcePath = "integrations/cfg/transfer/" + transferID + "/cmds";

    API.AddRecord(resourcePath, finalPayload).then((response) => {
      if (response.code === 200) {
        NotificationManager.success("Record added!", "Connection Entry", 10000);
        navigate("/FileCommands/" + transferID);
      } else {
        NotificationManager.error(response.message, "Failed!", 10000);
      }
    });
  };

  const handleNext = async () => {
    const current = sections[sections.findIndex((s) => s.id === activeId)];
    const valid = await trigger(current.fields);
    if (valid) goNext();
  };

  return (
    <>
      <AddFormLayout
        title="Add transfer command"
        subtitle={`For transfer ${transferID}`}
        breadcrumbs={[
          { label: "File transfers", onClick: onClickFileTrns },
          {
            label: transferID,
            mono: true,
            onClick: () => navigate(`/ConnectionCommands/${transferID}`),
          },
          { label: "Add command" },
        ]}
        sections={sections}
        activeSectionId={activeId}
        onSectionChange={setActiveId}
        secondaryLabel={isFirst ? undefined : "Previous"}
        onSecondaryClick={goPrev}
        primaryLabel={isLast ? "Add command" : "Next section"}
        onPrimaryClick={isLast ? handleSubmit(onSubmit) : handleNext}
      >
        {activeId === "basic" && (
          <SectionGrid>
            <TextField
              required
              label="Transfer name"
              size="small"
              fullWidth
              {...register("TRANSFER_NAME")}
              error={!!errors.TRANSFER_NAME}
              variant="outlined"
            />
            <TextField
              select
              label="State"
              size="small"
              fullWidth
              {...register("STATE")}
              error={!!errors.STATE}
              variant="outlined"
            >
              {stateItems.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="Project ID"
              size="small"
              fullWidth
              {...register("PROJECT_ID")}
              error={!!errors.PROJECT_ID}
              variant="outlined"
            />
            <TextField
              select
              label="Frequency"
              size="small"
              fullWidth
              {...register("FREQUENCY")}
              error={!!errors.FREQUENCY}
              variant="outlined"
            >
              {frequencyItems.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Command"
              size="small"
              fullWidth
              {...register("CMD")}
              error={!!errors.CMD}
              variant="outlined"
            >
              {itemsCommand.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="Cmd group"
              size="small"
              fullWidth
              {...register("CMD_GROUP")}
              error={!!errors.CMD_GROUP}
              variant="outlined"
            />
          </SectionGrid>
        )}

        {activeId === "source-dest" && (
          <SectionGrid>
            <TextField
              required
              label="Source"
              size="small"
              fullWidth
              {...register("SOURCE")}
              error={!!errors.SOURCE}
              variant="outlined"
            />
            <TextField
              required
              label="Destination"
              size="small"
              fullWidth
              {...register("DESTINATION")}
              error={!!errors.DESTINATION}
              variant="outlined"
            />
            <SectionGrid.FullWidth>
              <TextField
                required
                label="Filter"
                size="small"
                fullWidth
                {...register("FILTER")}
                error={!!errors.FILTER}
                variant="outlined"
              />
            </SectionGrid.FullWidth>
          </SectionGrid>
        )}

        {activeId === "paths" && (
          <SectionGrid>
            <SectionGrid.FullWidth>
              <TextField
                required
                label="From path"
                size="small"
                fullWidth
                {...register("FROM_PATH")}
                error={!!errors.FROM_PATH}
                variant="outlined"
              />
            </SectionGrid.FullWidth>
            <SectionGrid.FullWidth>
              <TextField
                required
                label="To path"
                size="small"
                fullWidth
                {...register("TO_PATH")}
                error={!!errors.TO_PATH}
                variant="outlined"
              />
            </SectionGrid.FullWidth>
          </SectionGrid>
        )}

        {activeId === "rules" && (
          <SectionGrid>
            <TextField
              select
              label="Archive"
              size="small"
              fullWidth
              {...register("ARCHIVE")}
              error={!!errors.ARCHIVE}
              variant="outlined"
            >
              {itemsArchive.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="File mask"
              size="small"
              fullWidth
              {...register("FILE_MASK")}
              error={!!errors.FILE_MASK}
              variant="outlined"
            >
              {itemsFileMask.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="Process"
              size="small"
              fullWidth
              {...register("PROCESS")}
              error={!!errors.PROCESS}
              variant="outlined"
            />
            <TextField
              select
              label="Required flag"
              size="small"
              fullWidth
              {...register("REQUIRED_FL")}
              error={!!errors.REQUIRED_FL}
              variant="outlined"
            >
              {itemsFlags.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Overwrite flag"
              size="small"
              fullWidth
              {...register("OVERWRITE_FL")}
              error={!!errors.OVERWRITE_FL}
              variant="outlined"
            >
              {itemsFlags.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Sub folders flag"
              size="small"
              fullWidth
              {...register("SUBFOLDERS_FL")}
              error={!!errors.SUBFOLDERS_FL}
              variant="outlined"
            >
              {itemsFlags.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="List limit"
              size="small"
              fullWidth
              {...register("LIST_LIMIT")}
              error={!!errors.LIST_LIMIT}
              variant="outlined"
            />
            <TextField
              required
              label="List order"
              size="small"
              fullWidth
              {...register("LIST_ORDER")}
              error={!!errors.LIST_ORDER}
              variant="outlined"
            />
            <TextField
              required
              label="Replaces"
              size="small"
              fullWidth
              {...register("REPLACES")}
              error={!!errors.REPLACES}
              variant="outlined"
            />
          </SectionGrid>
        )}
      </AddFormLayout>

      <ConfirmAddDialog
        open={!!pendingPayload}
        onClose={() => setPendingPayload(null)}
        onConfirm={handleConfirmAdd}
        itemLabel="connection command"
      />
    </>
  );
};

export default ConnectionCommandForm;
